import React, { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { useHistory } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../../../../hooks/redux';
import { useDateFsnLocale } from '../../../../hooks/useDateFsnLocale';
import { useCustomSnackbar } from '../../../../hooks/useCustomEnqueueSnackbar';
import DateVO from '../../../../core/valueObjects/DateVO';
import { userInfoApi } from '../../../../services/apiServices/user/userInfo';
import { IMultiAccount } from '../../../../models/UserInfo';
import {
  ButtonClassNames,
  AccountStatus,
  LocalStorageItems,
  GlobalRouteNames,
  AccountSettingRouteNames,
} from '../../../../models/Common';
import { BasicButton } from '../../buttons/BasicButton/BasicButton';
import { CustomSkeletonElement } from '../../CustomSkeletonElement';
import { CustomMUIButton } from '../../buttons/CustomMUIButton';
import { ReactComponent as CrownIcon } from '../../../../assets/images/IconCrown.svg';
import { ReactComponent as CheckIcon } from '../../../../assets/images/ArrowSaveEmail.svg';
import { getErrorMessage } from '../../../../utils';

interface ISelectAccount {
  userId: number | string | undefined;
  handleClose: () => void;
}

export const SelectAccount: FC<ISelectAccount> = ({ userId, handleClose }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { customEnqueueSnackbar } = useCustomSnackbar();
  const { selectedLanguage } = useAppSelector((state) => state.langReducer);
  const { fnsLocale } = useDateFsnLocale(selectedLanguage);
  const { data, isLoading, isError, error } = userInfoApi.useGetMultiaccountMembersQuery(selectedLanguage, {
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    if (isError && error) {
      const message = getErrorMessage(error);
      customEnqueueSnackbar(message, 'error');
    }
  }, [isError, error]);

  const handleSwitchAccount = (item: IMultiAccount) => {
    if (item.UserID === Number(userId)) {
      handleClose();
      return;
    }
    if (item.Role === 'member') {
      localStorage.setItem(LocalStorageItems.ControlledAccount, JSON.stringify(item));
    } else {
      localStorage.removeItem(LocalStorageItems.ControlledAccount);
    }
    dispatch(userInfoApi.util.invalidateTags(['Accounts']));
    handleClose();
  };

  const handleContinue = () => {
    history.push(`${GlobalRouteNames.AccountSettingPage}${AccountSettingRouteNames.MultiAccountPage}`);
    handleClose();
  };

  return (
    <div className="modals select-account">
      <div className="modals__header">
        <article className="modals__header__title">{t('multiAccount.selectAccount')}</article>
        {isError && <p className="modals__header__description">{t('paymentHistory.errorOccurred')}</p>}
      </div>
      {isLoading ? (
        <CustomSkeletonElement
          skeletonContainerClassName="skeleton-container modals__accounts__skeleton-container"
          skeletonChildrenClassName="skeleton-children skeleton modals__accounts__skeleton-children"
          childrenCount={2}
        />
      ) : (
        <div className="modals__accounts">
          {data && data.List.length
            ? data.List.filter((acc) => acc.Status === 'verified').map((item) => {
                return (
                  <BasicButton
                    key={item.id}
                    className="modals__accounts__user"
                    handleClick={() => handleSwitchAccount(item)}
                  >
                    <div className="modals__accounts__user-icon">
                      {item.Role === 'admin' ? <CrownIcon /> : item.User?.Name[0]}
                    </div>
                    <div className="modals__accounts__user-info">
                      <span className="modals__accounts__user-id">{item.UserID}</span> {item.Alias || item.User?.Name}
                      <br />
                      {item.Service?.Status === AccountStatus.Active ? (
                        <span className="modals__accounts__user-status-paid">
                          {t('serviceManagement.nextPaymentDate')}{' '}
                          {format(
                            DateVO.create(item.Service?.ExpireDate).get(true),
                            selectedLanguage === 'en' ? 'MMMM d, yyyy' : 'd MMMM yyyy',
                            { locale: fnsLocale },
                          )}
                        </span>
                      ) : (
                        <span className="modals__accounts__user-status-unpaid">{t('multiAccount.noPayment')}</span>
                      )}
                      <br />
                      {item.User?.Address}
                    </div>
                    {item.UserID === Number(userId) && <CheckIcon className="icon-check" />}
                  </BasicButton>
                );
              })
            : null}
        </div>
      )}
      <div className="modals__btn-group" style={{ width: '100%', justifyContent: 'stretch' }}>
        <CustomMUIButton
          label={t('multiAccount.manageAccounts')}
          handleClick={handleContinue}
          className={ButtonClassNames.SecondaryOutlined}
          isBold
        />
      </div>
    </div>
  );
};
